// ---------------------------------------------------------------------------
// Zwei Orte zugleich (PF 23, Roadmap AP-40): Eine Figur tritt in zwei Szenen auf, die sich in Weltzeit sicher überschneiden, aber an
// verschiedenen Schauplätzen spielen. Auftreten heißt: Perspektive der Szene oder Rede, Handlung bzw. Gedanke im Draft – eine bloße
// Erwähnung (an jemanden denken, von jemandem reden) zählt nicht. Sicher überschneiden: beide Zeiten vergleichbar und auf die Stunde
// genau (nicht „ungefähr“); zwei Zeitpunkte zur selben Stunde bzw. Minute, sonst überlappende Spannen.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { draftDocOf, povOf, worldSpanOf, type WorldSpan } from './narrative.js';
import { refIndex } from './refs.js';
import { isScene } from './scene.js';
import { placeOf } from './scenePlace.js';
import { spansIn, type SpanRole } from './spans.js';
import { stateRoleOf } from './states.js';
import { sameAt } from './textTime.js';
import type { Entity, IgnoredTerm, StoryNode, TimePrecision } from './types.js';

/** Eine Figur in zwei Szenen zugleich: hier (`sceneId`, Schauplatz `place`) und dort (`otherId`, `otherPlace`) */
export interface PlaceClash {
  entityId: string;
  sceneId: string;
  place: string;
  otherId: string;
  otherPlace: string;
}

const ON_STAGE: readonly SpanRole[] = ['rede', 'handlung', 'gedanke'];

/** Wer in einer Szene sicher auftritt: die Perspektive und Personen mit Rede, Handlung oder Gedanke im Draft */
export function onStage(ctx: StoryCtx, scene: StoryNode, refs: ReadonlyMap<string, Entity> = refIndex(ctx)): Set<string> {
  const out = new Set<string>();
  const pov = povOf(ctx, scene);
  if (pov) out.add(pov);
  for (const s of spansIn(draftDocOf(scene)?.text ?? '')) {
    if (!s.role || !ON_STAGE.includes(s.role)) continue;
    for (const k of s.keys) {
      const e = refs.get(k);
      if (e && stateRoleOf(ctx, e.id) === 'person') out.add(e.id);
    }
  }
  return out;
}

const fine = (n: StoryNode): boolean => !n.time?.approx && (n.time?.precision ?? 'minute') !== 'day' && n.time?.precision !== 'month' && n.time?.precision !== 'year';
const coarser = (a: StoryNode, b: StoryNode): TimePrecision => (a.time?.precision === 'hour' || b.time?.precision === 'hour' ? 'hour' : 'minute');

/** Überschneiden sich zwei Szenen sicher in Weltzeit? (Spannen `sa`, `sb` von `worldSpanOf`) */
function simultaneous(a: StoryNode, sa: WorldSpan, b: StoryNode, sb: WorldSpan): boolean {
  if (sa.basis !== sb.basis || !fine(a) || !fine(b)) return false;
  if (sa.start === sa.end && sb.start === sb.end) {
    const basis = sa.basis === 'ms' ? 'ms' : sa.basis === 'count' ? 'count' : null;
    return basis ? sameAt({ basis, value: sa.start, unit: null }, { basis, value: sb.start, unit: null }, coarser(a, b)) : sa.start === sb.start;
  }
  return sa.start < sb.end && sb.start < sa.end;
}

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** „Ist Absicht“ (ein Doppelgänger, eine Täuschung …): diese Figur in diesen beiden Szenen, gemerkt im Draft der einen */
export const placeClashIgnore = (ctx: StoryCtx, c: Pick<PlaceClash, 'entityId' | 'otherId'>): IgnoredTerm => ({ term: '~zugleich', before: `@${keyOf(ctx, c.entityId)}`, after: c.otherId });

const clashCache = new WeakMap<StoryCtx, ReadonlyMap<string, PlaceClash[]>>();

/**
 * Alle Figuren an zwei Orten zugleich, je Szene (beide Seiten eines Paars: an jeder der beiden Szenen). Ohne, was in einer der beiden
 * Szenen als Absicht gemerkt ist. Einmal je Stand der Geschichte berechnet.
 */
export function placeClashes(ctx: StoryCtx): ReadonlyMap<string, PlaceClash[]> {
  const cached = clashCache.get(ctx);
  if (cached) return cached;
  const refs = refIndex(ctx);
  const items = ctx.story.nodes
    .filter(isScene)
    .map((scene) => ({ scene, place: placeOf(ctx, scene), span: worldSpanOf(ctx, scene) }))
    .filter((x): x is { scene: StoryNode; place: string; span: WorldSpan } => !!x.place && !!x.span && fine(x.scene))
    .map((x) => ({ ...x, cast: onStage(ctx, x.scene, refs) }))
    .filter((x) => x.cast.size > 0)
    .sort((a, b) => (a.span.basis < b.span.basis ? -1 : a.span.basis > b.span.basis ? 1 : a.span.start - b.span.start));
  const ignored = (scene: StoryNode, entry: IgnoredTerm): boolean => !!draftDocOf(scene)?.ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);
  const out = new Map<string, PlaceClash[]>();
  const add = (c: PlaceClash): void => void out.set(c.sceneId, [...(out.get(c.sceneId) ?? []), c]);
  for (let i = 0; i < items.length; i++) {
    const a = items[i]!;
    // nach Beginn geordnet: was danach beginnt, kann sich nur überschneiden, solange es nicht nach dem Ende von a beginnt (Zeitpunkte
    // zur selben Stunde: bis eine Stunde danach)
    const hour = a.span.basis === 'ms' ? 3_600_000 : a.span.basis === 'count' ? 60 : 0;
    for (let j = i + 1; j < items.length && items[j]!.span.basis === a.span.basis && items[j]!.span.start <= a.span.end + hour; j++) {
      const b = items[j]!;
      if (b.place === a.place || !simultaneous(a.scene, a.span, b.scene, b.span)) continue;
      for (const id of a.cast) {
        if (!b.cast.has(id)) continue;
        if (ignored(a.scene, placeClashIgnore(ctx, { entityId: id, otherId: b.scene.id })) || ignored(b.scene, placeClashIgnore(ctx, { entityId: id, otherId: a.scene.id }))) continue;
        add({ entityId: id, sceneId: a.scene.id, place: a.place, otherId: b.scene.id, otherPlace: b.place });
        add({ entityId: id, sceneId: b.scene.id, place: b.place, otherId: a.scene.id, otherPlace: a.place });
      }
    }
  }
  clashCache.set(ctx, out);
  return out;
}
